// -----------------------------------------------------------------------------
// NextActionPanel
//
// Action panel body for LEAD_NEXT_ACTION / BOOK_APPOINTMENT.
//
// Shows a 5-option outcome picker. Archive is fully wired: selecting it reveals
// an inline sub-form (reason + notes), clicking "Archive lead" dispatches
// saveNewLeadDetail → ArchivedLeadDashBoard which creates the archive note and
// closes the lead to communication via updatePersonalDetail (V1 path).
//
// Follow-up is fully wired: selecting it reveals a date picker sub-form. On
// confirm, a FOLLOW_UP reminder is created via the V1 /reminders/ endpoint
// (same mechanism as ScheduleReminderPanel) with the selected date, then the
// checklist item is marked done.
//
// Archive reasons come from state.schema.archiveReasons (filtered to only the
// BE-valid subset — see schema.js ARCHIVE_VALID_REASONS).
//
// The other 3 outcomes record the user's intention and mark the checklist item
// done via onComplete. Full status-transition wiring is deferred — those thunks
// require appointment IDs, treatment IDs, and complex dialog flows that are
// outside the scope of this panel.
// -----------------------------------------------------------------------------
import PropTypes from 'prop-types';
import { useState } from 'react';
import {
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
} from '@mui/material';
import Iconify from 'src/components/Iconify';
import { useSelector, dispatch } from 'src/redux/store';
import { saveNewLeadDetail, ArchivedLeadDashBoard } from 'src/redux/slices/lead';
import { AddReminder } from 'src/redux/slices/lead/taskThunks';
import { updateError } from 'src/redux/slices/user';
import { generateReasonsLabel } from 'src/utils/getReferralData';

/** Returns ISO datetime string for tomorrow at 09:00 local time (truncated to minute). */
function defaultFollowUpDate() {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(9, 0, 0, 0);
    return d.toISOString().slice(0, 16); // "YYYY-MM-DDTHH:MM" for datetime-local input
}

const OUTCOMES = [
    { value: 'treatment_start',    label: 'Starting treatment',   icon: 'mdi:tooth-outline',            color: '#34c759' },
    { value: 'clincheck',          label: 'ClinCheck',            icon: 'mdi:clipboard-check-outline',  color: '#5B8DEF' },
    { value: 'follow_up',          label: 'Follow up',            icon: 'mdi:phone-outline',            color: '#FFAB00' },
    { value: 'treatment_complete', label: 'Treatment complete',   icon: 'mdi:check-circle-outline',     color: '#00B8D9' },
    { value: 'archive',            label: 'Archive',              icon: 'mdi:archive-outline',          color: '#FF5630' },
];

export default function NextActionPanel({ item, practiceId, leadId, onComplete, onClose, leadDetail, handleReloadLeads }) {
    const [selected, setSelected] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveNotes, setArchiveNotes] = useState('');
    const [followUpDate, setFollowUpDate] = useState(defaultFollowUpDate);

    // Archive reasons come from Redux — only the BE-valid subset after schema.js filtering
    const rawArchiveReasons = useSelector((state) => state.schema?.archiveReasons) || [];
    const archiveReasonOptions = rawArchiveReasons.map((value) => ({
        value,
        label: generateReasonsLabel(value),
    }));

    const isArchive = selected === 'archive';
    const isFollowUp = selected === 'follow_up';

    // Confirm is invalid if nothing selected, archive selected but no reason chosen,
    // or follow-up selected but no date entered.
    const archiveInvalid = isArchive && !archiveReason;
    const followUpInvalid = isFollowUp && !followUpDate;
    const confirmDisabled = !selected || submitting || archiveInvalid || followUpInvalid;

    const handleConfirm = () => {
        if (confirmDisabled) return;

        if (isArchive) {
            setSubmitting(true);
            const archiveLeadId = leadDetail?.id || null;
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;

            const addNotesObj = {
                note: archiveReason,
                treatment: treatmentId,
                additional_notes: archiveNotes,
            };

            // V1-style payload — mirrors Archive.jsx onSubmit exactly.
            // openCommunication triggers coreThunks to call updatePersonalDetail
            // (sets is_open_for_communication: false) after the note is created.
            dispatch(saveNewLeadDetail({
                archiveLead: {
                    notes: addNotesObj,
                    openCommunication: { id: archiveLeadId, is_open_for_communication: false },
                    archiveData: { note: archiveNotes },
                },
            }));

            const handleClose = () => {
                setSubmitting(false);
                // Toast is dispatched by coreThunks (openCommunication path) — do not duplicate here.
                onComplete?.({ ...item, resolvedAction: 'archive' });
                onClose();
                // Deferred reload — Celery processes the archive asynchronously.
                // Wait 3s so the table refresh hits after the worker drains.
                setTimeout(() => handleReloadLeads?.(), 3000);
            };

            dispatch(ArchivedLeadDashBoard(handleClose, leadDetail));
            return;
        }

        // Follow-up: create a FOLLOW_UP reminder with the selected date, then mark done.
        // Mirrors ScheduleReminderPanel — creates a V1 reminder at /reminders/ which the
        // bridge translates into a V2 task with due_at set to the selected datetime.
        if (isFollowUp) {
            if (!practiceId || !leadId) {
                // Fallback: mark done without creating reminder if IDs are missing.
                onComplete?.({ ...item, resolvedAction: 'follow_up' });
                return;
            }
            setSubmitting(true);
            const treatmentId = leadDetail?.lead_treatments?.[0]?.id || null;
            const reminderData = {
                lead: leadId,
                datetime: new Date(followUpDate).toISOString(),
                group: 'FOLLOW_UP',
                method: 'PHONE',
                ...(treatmentId ? { lead_treatment: treatmentId } : {}),
            };
            dispatch(AddReminder(reminderData, practiceId, ({ success }) => {
                setSubmitting(false);
                if (success) {
                    onComplete?.({ ...item, resolvedAction: 'follow_up' });
                } else {
                    dispatch(updateError({ success: false, message: 'Failed to schedule follow-up — please try again.' }));
                }
            }));
            return;
        }

        // Non-archive, non-follow-up outcomes: record intention + mark checklist item done.
        // Full status-transition thunks are deferred — they require IDs and flows
        // that must be driven from the main popup controls, not from here.
        //
        // TODO (treatment_start):    needs appointment ID + StartingTreatment thunk
        // TODO (clincheck):          needs ClinCheck record ID + thunk
        // TODO (treatment_complete): needs CustomCompleteTreatmentDialog (treatment fees)
        //
        // resolvedAction is attached so handlePanelComplete can forward it to the
        // completeChecklistItem thunk as { resolved_action } — required by the BE
        // for LEAD_NEXT_ACTION cells.
        onComplete?.({ ...item, resolvedAction: selected });
    };

    // Confirm button appearance — resolved once to avoid nested ternaries (no-nested-ternary).
    let confirmIcon = 'mdi:check';
    if (isArchive) confirmIcon = 'mdi:archive-outline';
    else if (isFollowUp) confirmIcon = 'mdi:calendar-check-outline';

    let confirmBgColor = '#5B8DEF';
    if (isArchive) confirmBgColor = '#FF5630';
    else if (isFollowUp) confirmBgColor = '#B76E00';

    let confirmHoverColor = '#4a7de0';
    if (isArchive) confirmHoverColor = '#d9472b';
    else if (isFollowUp) confirmHoverColor = '#9a5c00';

    let confirmDisabledColor;
    if (isArchive) confirmDisabledColor = 'rgba(255,86,48,0.4)';
    else if (isFollowUp) confirmDisabledColor = 'rgba(183,110,0,0.4)';

    let confirmLabel = 'Confirm';
    if (isArchive) confirmLabel = 'Archive lead';
    else if (isFollowUp) confirmLabel = 'Schedule follow-up';

    return (
        <Box
            data-testid="next-action-panel"
            sx={{
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 1.75,
                // No height:'100%' — auto height avoids conflicts when the action-panel
                // container has no definite height (flex auto-height chain).
                // No overflowY:'auto' — let the outer popup scroll if needed.
            }}
        >
            {/* ── Header ── */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                <Box
                    sx={{
                        width: 36,
                        height: 36,
                        borderRadius: '10px',
                        bgcolor: 'rgba(91,141,239,0.10)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                    }}
                >
                    <Iconify icon="mdi:directions-fork" width={18} height={18} sx={{ color: '#5B8DEF' }} />
                </Box>
                <Box>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600, color: '#1d1d1f', letterSpacing: '-0.012em' }}>
                        What happens next?
                    </Typography>
                    <Typography sx={{ fontSize: '12px', color: '#6e6e73', letterSpacing: '-0.005em' }}>
                        Select the most likely outcome
                    </Typography>
                </Box>
            </Box>

            {/* ── Outcome chips ── */}
            <Stack spacing={1}>
                {OUTCOMES.map(({ value, label, icon, color }) => {
                    const isSelected = selected === value;
                    return (
                        <Button
                            key={value}
                            data-testid={`next-action-option-${value}`}
                            variant={isSelected ? 'contained' : 'outlined'}
                            disableElevation
                            onClick={() => {
                                setSelected(value);
                                // Reset sub-forms when switching away
                                if (value !== 'archive') {
                                    setArchiveReason('');
                                    setArchiveNotes('');
                                }
                                if (value !== 'follow_up') {
                                    setFollowUpDate(defaultFollowUpDate());
                                }
                            }}
                            startIcon={<Iconify icon={icon} width={16} height={16} />}
                            sx={{
                                justifyContent: 'flex-start',
                                textTransform: 'none',
                                borderRadius: '8px',
                                fontSize: '13px',
                                fontWeight: isSelected ? 600 : 400,
                                borderColor: isSelected ? color : 'rgba(0,0,0,0.12)',
                                bgcolor: isSelected ? color : 'transparent',
                                color: isSelected ? '#fff' : '#1d1d1f',
                                '&:hover': {
                                    bgcolor: isSelected ? color : `${color}14`,
                                    borderColor: color,
                                },
                            }}
                        >
                            {label}
                        </Button>
                    );
                })}
            </Stack>

            {/* ── Archive sub-form (shown when archive outcome is selected) ── */}
            {isArchive && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255,86,48,0.04)',
                        border: '1px solid rgba(255,86,48,0.18)',
                        // flexShrink:0 prevents the sub-form from being squashed by sibling items
                        flexShrink: 0,
                    }}
                >
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#FF5630', letterSpacing: '-0.005em' }}>
                        Archive details
                    </Typography>

                    <FormControl fullWidth size="small" required>
                        <InputLabel id="archive-reason-label">Archive reason</InputLabel>
                        <Select
                            labelId="archive-reason-label"
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                            label="Archive reason"
                            inputProps={{ 'data-testid': 'archive-reason-select' }}
                        >
                            {archiveReasonOptions.map(({ value, label }) => (
                                <MenuItem key={value} value={value} sx={{ fontSize: '13px' }}>
                                    {label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        multiline
                        rows={2}
                        size="small"
                        fullWidth
                        label="Notes (optional)"
                        value={archiveNotes}
                        onChange={(e) => setArchiveNotes(e.target.value)}
                        inputProps={{ 'data-testid': 'archive-notes-input' }}
                        sx={{ '& .MuiInputBase-root': { fontSize: '13px' } }}
                    />
                </Box>
            )}

            {/* ── Follow-up date sub-form (shown when follow_up outcome is selected) ── */}
            {isFollowUp && (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1.5,
                        p: 1.5,
                        borderRadius: '10px',
                        bgcolor: 'rgba(255, 171, 0, 0.04)',
                        border: '1px solid rgba(255, 171, 0, 0.20)',
                        flexShrink: 0,
                    }}
                >
                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#B76E00', letterSpacing: '-0.005em' }}>
                        Schedule follow-up
                    </Typography>
                    <Box>
                        <Typography sx={{ fontSize: '11px', color: '#6e6e73', mb: 0.5, letterSpacing: '-0.003em' }}>
                            Follow-up date &amp; time
                        </Typography>
                        <TextField
                            type="datetime-local"
                            size="small"
                            fullWidth
                            value={followUpDate}
                            onChange={(e) => setFollowUpDate(e.target.value)}
                            inputProps={{
                                'data-testid': 'follow-up-date-input',
                                min: new Date().toISOString().slice(0, 16),
                            }}
                            sx={{ '& .MuiInputBase-root': { fontSize: '13px' } }}
                        />
                    </Box>
                </Box>
            )}

            {/* ── Info note — hidden while archive or follow-up sub-form is showing ── */}
            {!isArchive && !isFollowUp && (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 0.75,
                        p: 1.25,
                        bgcolor: 'rgba(0,0,0,0.03)',
                        borderRadius: '8px',
                        // mt:'auto' was a no-op in auto-height containers and caused
                        // overlap with the actions row in height-constrained contexts.
                    }}
                >
                    <Iconify icon="mdi:information-outline" width={14} height={14} sx={{ color: '#6e6e73', mt: 0.125, flexShrink: 0 }} />
                    <Typography sx={{ fontSize: '11px', color: '#6e6e73', letterSpacing: '-0.003em', lineHeight: 1.4 }}>
                        Selecting an outcome records your intention and marks this step done. Status transitions happen through the main popup controls.
                    </Typography>
                </Box>
            )}

            {/* ── Actions ── */}
            <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                <Button
                    data-testid="next-action-confirm"
                    variant="contained"
                    disableElevation
                    onClick={handleConfirm}
                    disabled={confirmDisabled}
                    startIcon={
                        submitting
                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                            : <Iconify icon={confirmIcon} width={14} height={14} />
                    }
                    sx={{
                        flex: 1,
                        textTransform: 'none',
                        borderRadius: '8px',
                        fontSize: '13px',
                        fontWeight: 600,
                        bgcolor: confirmBgColor,
                        '&:hover': { bgcolor: confirmHoverColor },
                        '&.Mui-disabled': { bgcolor: confirmDisabledColor },
                        boxShadow: 'none',
                    }}
                >
                    {confirmLabel}
                </Button>
                <Button
                    variant="outlined"
                    size="small"
                    onClick={onClose}
                    disabled={submitting}
                    sx={{ textTransform: 'none', borderRadius: '8px', fontSize: '13px' }}
                >
                    Cancel
                </Button>
            </Stack>
        </Box>
    );
}

NextActionPanel.propTypes = {
    item: PropTypes.object,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    leadId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onComplete: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    /** Lead object passed as followLead to ArchivedLeadDashBoard (provides lead id + treatments) */
    leadDetail: PropTypes.object,
    /** Deferred table reload — called ~3s after archive dispatched so Celery drains first. */
    handleReloadLeads: PropTypes.func,
};

NextActionPanel.defaultProps = {
    item: null,
    practiceId: null,
    leadId: null,
    onComplete: null,
    leadDetail: null,
    handleReloadLeads: null,
};
